import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from 'reactstrap'

import * as S from './styles'
import SingleForm from 'components/SingleForm'
import moment from 'moment'
import SelectOptions from 'components/SelectOptions'
import { compareValue, getLoginVehicleController } from 'utils/commonFunctions'
import { uniqBy } from 'lodash'
import loginStorage from 'utils/loginStorage'
import { CONDITION_ALL as DEFAULT_CONDITION_ALL, DEFAULT_FILTER_SORT } from 'constants/misc'
import DetailFilterSearchBooking from './DetailFilterAndSort'

export type SortConditionType = typeof SortCondition[number]['value'];

export interface FilterConditionType {
  status?: string;
  startDate?: string;
  endDate?: string;
  operationType?: string;
  vehicleControllerCompanyId?: string;
  driverCompanyId?: string;
  vehicleCategoryIds?: Array<string>;
}

export interface CompanyI {
  company_id: string;
  company_name: string;
  [key: string]: any;
}

interface UserDataI {
  attributes?: {
    company_id: string;
    company_name: string;
    [key: string]: any;
  };
  company_id?: string;
  company_name?: string;
  [key: string]: any;
}

export interface VehicleCategoryI {
  parameter_id: string | number;
  parameter_name: string;
  [key: string]: any;
}

type Props = {
  canUnmannedCheckin?: boolean
  totalItem?: number
  tenantId: string
  sortCondition: SortConditionType
  filterCondition: FilterConditionType
  companyList: CompanyI[]
  userData: UserDataI
  userRoles: Record<string, any>
  vehicleCategories: VehicleCategoryI[]
  getCompanyList: ({ callback }: { callback?: () => void }) => void
  updateFilterSearchBooking: (filter: { filter?: FilterConditionType, sort?: SortConditionType }) => void
  onSubmitFilterSort(onCloseBookingSearch: () => void): void
  oneResetFilter(): void
}

export const CONDITION_ALL = DEFAULT_CONDITION_ALL

export const SortCondition = [
  {
    label: 'ステータス順',
    value: CONDITION_ALL,
  },
  {
    label: '作成順',
    value: 'created_date',
  },
  {
    label: '予約開始日時順',
    value: 'start_date',
  },
  {
    label: '受付時間順',
    value: 'checkin_date',
  },
  {
    label: '滞留時間順',
    value: 'general_booking_stay_time',
  },
  {
    label: '指示時刻順',
    value: 'entry_instruction_time',
  },
] as const

export const FilterStatus = [
  {
    label: '予約',
    value: 'plan',
  },
  {
    label: '受付済み',
    value: 'checkin',
  },
  {
    label: 'すべて',
    value: CONDITION_ALL,
  },
] as const

export const ImportExportFiler = [
  {
    label: '積み',
    value: 'export', // 拠点から荷物が `export` なので積み
  },
  {
    label: '卸し',
    value: 'import', // 拠点から荷物が `import` なので卸し
  },
  {
    label: '両方',
    value: CONDITION_ALL,
  },
]

const FilterSearchBooking: FC<Props> = ({
  canUnmannedCheckin,
  totalItem,
  tenantId,
  userData,
  userRoles,
  companyList,
  vehicleCategories,
  filterCondition,
  sortCondition,
  getCompanyList,
  updateFilterSearchBooking,
  onSubmitFilterSort,
  oneResetFilter,
}) => {
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [sortDraft, setSortDraft] = useState<SortConditionType>(sortCondition || DEFAULT_FILTER_SORT.sort)

  const allVehicleCategoryIds = useMemo(() => {
    return (vehicleCategories || [])
      ?.filter((it) => it.display_flag)
      ?.map((it) => String(it.parameter_id))
  }, [vehicleCategories])

  const handleClearFilterAndSort = useCallback(() => {
    setSortDraft(DEFAULT_FILTER_SORT.sort as SortConditionType)

    return updateFilterSearchBooking({
      filter: {
        ...DEFAULT_FILTER_SORT.filter as FilterConditionType,
        vehicleCategoryIds: [...allVehicleCategoryIds, CONDITION_ALL],
      },
    })
  }, [allVehicleCategoryIds, updateFilterSearchBooking])

  const handleCloseModalFilter = useCallback(() => {
    setOpenModal(false)
    const beforeState = loginStorage.getFilterSortBookingNormal(tenantId)
    updateFilterSearchBooking({
      ...beforeState
    })
  }, [tenantId, updateFilterSearchBooking])

  const handleOpenModalFilter = useCallback(() => {
    setOpenModal(true)

    // Load previous filter and sort state from loginStorage
    // If no previous state, use default filter and sort
    const beforeState = loginStorage.getFilterSortBookingNormal(tenantId)
    setSortDraft(beforeState?.sort || DEFAULT_FILTER_SORT.sort)

    // If vehicleCategoryIds is contains CONDITION_ALL, set it to all vehicle categories
    const isChooseAllVehicleCategory = beforeState?.filter?.vehicleCategoryIds?.includes(CONDITION_ALL)
    const vehicleCategoryIds = isChooseAllVehicleCategory ? [
      ...allVehicleCategoryIds,
      CONDITION_ALL
    ] : beforeState?.filter?.vehicleCategoryIds || []

    updateFilterSearchBooking({
      ...beforeState,
      filter: {
        ...beforeState.filter,
        vehicleCategoryIds: (vehicleCategoryIds || [])?.length > 0 ? vehicleCategoryIds : []
      }
    })

    // Fetch company list when opening the modal
    // and set vehicleControllerCompanyId and driverCompanyId against the filter
    getCompanyList({
      callback: () => {
        updateFilterSearchBooking({
          filter: {
            ...beforeState.filter,
            vehicleControllerCompanyId: beforeState?.filter?.vehicleControllerCompanyId || '',
            driverCompanyId: beforeState?.filter?.driverCompanyId || '',
            vehicleCategoryIds: (vehicleCategoryIds || [])?.length > 0 ? vehicleCategoryIds : []
          }
        })
      }
    })
  }, [tenantId, allVehicleCategoryIds, updateFilterSearchBooking, getCompanyList])

  const companyOptions = useMemo(() => {
    const user = getLoginVehicleController(userRoles, userData)

    const companyId = 'attributes' in user ? user.attributes.company_id : user.company_id
    const companyName = 'attributes' in user ? user.attributes.company_name : user.company_name

    const currentOptions = [{ value: companyId, label: companyName }]

    const options = companyList
      ?.map((it) => ({ value: it.company_id, label: it.company_name }))
      ?.concat(currentOptions)

    const finalOptions = uniqBy(
      options?.filter((it) => it.value),
      (it) => it.value.toString()
    )

    const unassignOptions = { value: '', label: '選択してください' }
    return [unassignOptions].concat(finalOptions)
  }, [companyList, userRoles, userData])

  const vehicleCategoryOptions = useMemo(() => {
    const optionsValue = (vehicleCategories || [])
      ?.filter(
        (it) => it.display_flag)
      ?.map((it) => ({
        label: it.parameter_name,
        value: String(it.parameter_id),
      }))
    return (optionsValue || []).concat({ label: 'すべて', value: CONDITION_ALL })
  }, [vehicleCategories])

  const handleUpdateFilter = (name: string, value: string | number) => {
    const { startDate, endDate } = filterCondition || {}
    if ((name === 'startDate' && endDate) || (name === 'endDate' && startDate)) {
      const isUpdateStartDate = (name === 'endDate' && moment(value)?.isBefore(startDate))
      const isUpdateEndDate = (name === 'startDate' && moment(value)?.isAfter(endDate))

      if (isUpdateStartDate || isUpdateEndDate) {
        updateFilterSearchBooking({
          filter: {
            ...filterCondition,
            startDate: moment(value)?.format('YYYY-MM-DD'),
            endDate: moment(value)?.format('YYYY-MM-DD'),
          }
        })
        return
      }
    }

    const isUpdateDate = (name === 'startDate' || name === 'endDate')
    updateFilterSearchBooking({
      filter: {
        ...filterCondition,
        [name]: isUpdateDate ? moment(value)?.format("YYYY-MM-DD") : String(value),
      }
    })
  }

  const handleUpdateFilterCategory = (value: string, isCheck: boolean) => {
    const isCheckAll = value === CONDITION_ALL
    const currentIds = filterCondition?.vehicleCategoryIds || []

    const getUpdatedIds = () => {
      // If checking "すべて", return all vehicle category IDs and CONDITION_ALL
      // If unchecking "すべて", return an empty array
      if (isCheckAll) {
        return isCheck ? [...allVehicleCategoryIds, CONDITION_ALL] : []
      }

      // If checking or unchecking a specific category, update the IDs accordingly
      let updatedIds = isCheck
        ? [...currentIds, value]
        : currentIds?.filter(id => !compareValue(id, value))

      // Remove CONDITION_ALL when unchecking any category
      if (!isCheck && updatedIds?.includes(CONDITION_ALL)) {
        updatedIds = updatedIds?.filter(id => !compareValue(id, CONDITION_ALL))
      }

      // Add CONDITION_ALL if all categories are checked
      if (updatedIds?.length === allVehicleCategoryIds?.length) {
        updatedIds.push(CONDITION_ALL)
      }

      return updatedIds || []
    }

    const vehicleCategoryIds = getUpdatedIds()

    updateFilterSearchBooking({
      filter: {
        ...filterCondition,
        vehicleCategoryIds: vehicleCategoryIds?.length > 0 ? vehicleCategoryIds : []
      }
    })
  }

  const handleUpdateSort = (value: SortConditionType) => {
    setSortDraft(value)
  }

  const handleSubmitFilterSort = () => {
    updateFilterSearchBooking({
      sort: sortDraft,
    })
    loginStorage.setFilterSortBookingNormal(tenantId, ({
      filter: filterCondition,
      sort: sortDraft,
    }))
    onSubmitFilterSort(() => setOpenModal(false))
  }

  const renderModal = () => {
    return (
      <S.MessageModal isOpen={openModal} modalClassName={openModal ? 'modal-base-style' : ''}>
        <S.ModalContent>
          <S.ModalHeader>
            <h5>フィルター設定</h5>
            <Button outline={false} color="none" onClick={handleCloseModalFilter}>
              閉じる
            </Button>
          </S.ModalHeader>
          <S.ModalBody>
            <div>
              <S.LabelFilter>並び替え条件</S.LabelFilter>
              <S.WrapperListItem className="border-bottom">
                {SortCondition
                  // 自主荷役の場合総合受付滞在時間順を表示しない
                  .filter((sort) => {
                    if (!canUnmannedCheckin) return true

                    return sort.value !== 'general_booking_stay_time'
                  })
                  .map((sort) => (
                  <S.FilterItem key={sort.value} htmlFor={`sort-condition-${sort.value}`}>
                    <S.RadioFilter
                      id={`sort-condition-${sort.value}`}
                      type="radio"
                      name="sort"
                      onChange={() => handleUpdateSort(sort.value)}
                      checked={sort.value === sortDraft}
                    />
                    {sort.label}
                  </S.FilterItem>
                ))}
              </S.WrapperListItem>
            </div>

            <div>
              <S.LabelFilter>ステータス</S.LabelFilter>
              <S.WrapperListItem className="border-bottom">
                {FilterStatus.map((filter) => (
                  <S.FilterItem key={filter.value} htmlFor={`filter-status-${filter.value}`}>
                    <S.RadioFilter
                      id={`filter-status-${filter.value}`}
                      type="radio"
                      name="status"
                      onChange={() => handleUpdateFilter('status', filter.value)}
                      checked={filter.value === filterCondition.status}
                    />
                    {filter.label}
                  </S.FilterItem>
                ))}
              </S.WrapperListItem>
            </div>

            <div>
              <S.LabelFilter>受付日・予約開始日</S.LabelFilter>
              <S.WrapperListItem className="border-bottom">
                <S.CustomInputDate
                  id="filter-start-date"
                  name="startDate"
                  label=""
                  className="mr-2"
                  placeholder='開始日'
                  value={filterCondition.startDate || ''}
                  onChange={(value: string) => handleUpdateFilter('startDate', value)}
                  isDayPicker
                />
                <span className="mb-2 mr-2 font-weight-bold">~</span>
                <S.CustomInputDate
                  id="filter-end-date"
                  name="endDate"
                  label=""
                  placeholder='終了日'
                  value={filterCondition.endDate || ''}
                  onChange={(value: string) => handleUpdateFilter('endDate', value)}
                  isDayPicker
                />
              </S.WrapperListItem>
            </div>

            <div>
              <S.LabelFilter>積み卸し</S.LabelFilter>
              <S.WrapperListItem className="border-bottom">
                {ImportExportFiler.map((filter) => (
                  <S.FilterItem key={filter.value} htmlFor={`filter-import-export-${filter.value}`}>
                    <S.RadioFilter
                      id={`filter-import-export-${filter.value}`}
                      type="radio"
                      name="operationType"
                      onChange={() => handleUpdateFilter('operationType', filter.value)}
                      checked={filter.value === filterCondition.operationType}
                    />
                    {filter.label}
                  </S.FilterItem>
                ))}
              </S.WrapperListItem>
            </div>

            <div>
              <S.LabelFilter>配車担当会社名</S.LabelFilter>
              <S.WrapperListItem className="border-bottom">
                <SelectOptions
                  isAllowEmpty
                  name="vehicleControllerCompanyId"
                  width="200px"
                  isSearchable
                  options={companyOptions}
                  selectValue={filterCondition.vehicleControllerCompanyId || ''}
                  handleUpdateData={(value: string) => handleUpdateFilter('vehicleControllerCompanyId', value)}
                  hasClass={true}
                />
              </S.WrapperListItem>
            </div>

            <div>
              <S.LabelFilter>運送会社名</S.LabelFilter>
              <S.WrapperListItem className="border-bottom">
                <SelectOptions
                  isAllowEmpty
                  name="driverCompanyId"
                  width="200px"
                  isSearchable
                  options={companyOptions}
                  selectValue={filterCondition.driverCompanyId || ''}
                  handleUpdateData={(value: string) => handleUpdateFilter('driverCompanyId', value)}
                  hasClass={true}
                />
              </S.WrapperListItem>
            </div>

            <div>
              <S.LabelFilter>車両形態</S.LabelFilter>
              <S.WrapperListItem className="pb-0 mb-0">
                {vehicleCategoryOptions.map((filter) => (
                  <S.FilterItem key={filter.value} htmlFor={`filter-vehicle-category-${filter.value}`}>
                    <SingleForm
                      className="ml-2"
                      id={`filter-vehicle-category-${filter.value}`}
                      name='vehicleCategoryIds'
                      type="checkbox"
                      label={filter.label}
                      value={(filterCondition?.vehicleCategoryIds || [])?.includes(filter?.value?.toString())}
                      onChange={(val: boolean) => handleUpdateFilterCategory(filter.value, val)}
                      isCheckType
                    />
                  </S.FilterItem>
                ))}
              </S.WrapperListItem>
            </div>
          </S.ModalBody>

          <S.ModalFooter>
            <Button color="primary" onClick={handleSubmitFilterSort}>
              適用
            </Button>
            <Button color="light" onClick={handleClearFilterAndSort}>
              リセット
            </Button>
          </S.ModalFooter>
        </S.ModalContent>
      </S.MessageModal>
    )
  }

  return (
    <>
      <S.FilterSearchWrapper>
        <div className="d-flex align-items-center">
          <S.ButtonWrapper onClick={handleOpenModalFilter}>
            <S.ButtonAll>フィルター</S.ButtonAll>
          </S.ButtonWrapper>
          {renderModal()}
          <S.TextCheckinLabel>{canUnmannedCheckin ? '受付履歴' : '未割当台数'}</S.TextCheckinLabel>
        </div>
        <div className="d-flex align-items-center">
          <S.NumberPendingStyled>{totalItem}</S.NumberPendingStyled>台
        </div>
      </S.FilterSearchWrapper>

      <DetailFilterSearchBooking
        openModal={openModal}
        tenantId={tenantId}
        filterCondition={filterCondition}
        sortCondition={sortCondition}
        companyList={companyList}
        vehicleCategories={vehicleCategories}
        getCompanyList={() => getCompanyList({})}
        updateFilterSearchBooking={updateFilterSearchBooking}
        oneResetFilter={oneResetFilter}
      />
    </>
  )
}

export default memo(FilterSearchBooking)
